import { useMemo, useState } from "react";
import { shortHash } from "../../lib/format.js";
import {
  omissionFindingHref,
  omissionIsFinding,
  omissionReasonLabel,
} from "./threat-model-projection.js";

const PAGE = 40;

export function ThreatModelOmissionQueue({
  omissions,
  scanId,
}: {
  omissions: readonly { sourceId: string; reason: string; detail: string; sourceKind?: string }[];
  scanId?: string | undefined;
}) {
  const [query, setQuery] = useState("");
  const [reason, setReason] = useState("");
  const [page, setPage] = useState(1);
  const reasons = useMemo(
    () => [...new Set(omissions.map((item) => item.reason))].sort(),
    [omissions],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return omissions.filter((item) => {
      if (reason && item.reason !== reason) return false;
      if (!needle) return true;
      return `${item.sourceId} ${item.reason} ${item.detail}`.toLocaleLowerCase().includes(needle);
    });
  }, [omissions, query, reason]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE));
  const visiblePage = Math.min(page, pageCount);
  const rows = filtered.slice((visiblePage - 1) * PAGE, visiblePage * PAGE);

  return (
    <section className="threat-model-omission-queue" data-testid="threat-model-omission-queue">
      <div className="threat-model-omission-queue__header">
        <div>
          <span className="page-kicker">Work queue</span>
          <h3>Evidence kept out of the graph</h3>
          <p>
            These records stay visible without becoming architecture. An omitted finding is not a
            hop, and a long list is not a clean bill of health.
          </p>
        </div>
        <strong>{filtered.length}</strong>
      </div>
      <div className="threat-model-omission-queue__tools">
        <label>
          Search
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="source id, reason, detail"
          />
        </label>
        <label>
          Reason
          <select
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All reasons</option>
            {reasons.map((item) => (
              <option key={item} value={item}>
                {omissionReasonLabel(item)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {rows.length ? (
        <ul className="threat-model-omission-queue__list">
          {rows.map((item) => (
            <li key={`${item.sourceId}:${item.reason}`}>
              <code>{shortHash(item.sourceId, 16)}</code>
              <span>{omissionReasonLabel(item.reason)}</span>
              <p>{item.detail}</p>
              {omissionIsFinding(item) ? (
                <a href={omissionFindingHref(item.sourceId, scanId)}>Open finding</a>
              ) : (
                <small>Graph fact — not a finding row</small>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted-copy">No omitted records match this filter.</p>
      )}
      {pageCount > 1 ? (
        <nav className="pagination" aria-label="Omitted evidence pages">
          <button
            className="button button--small"
            type="button"
            disabled={visiblePage === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span>
            Page {visiblePage} of {pageCount}
          </span>
          <button
            className="button button--small"
            type="button"
            disabled={visiblePage === pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
          >
            Next
          </button>
        </nav>
      ) : null}
    </section>
  );
}
